import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'
import { useFilterStore } from '@/stores/filterStore'
import { matchTask, matchTS, inPeriod } from '@/lib/filters'
import { SLA_THRESHOLDS } from '@/lib/calculations'
import type { Task, Timesheet } from '@/types'

export function useFilteredData() {
  const { tasks, timesheets, activeEngineers, activeProjectIds } = useDashboardData()
  const category = useFilterStore(s => s.category)
  const engineer = useFilterStore(s => s.engineer)
  const period = useFilterStore(s => s.period)
  const customDateFrom = useFilterStore(s => s.customDateFrom)
  const customDateTo = useFilterStore(s => s.customDateTo)

  const filterParams = useMemo(() => ({
    category, engineer, period, customDateFrom, customDateTo,
    activeEngineers, activeProjectIds,
  }), [category, engineer, period, customDateFrom, customDateTo, activeEngineers, activeProjectIds])

  const filteredTasks = useMemo(() => tasks.filter(t => matchTask(t, filterParams)), [tasks, filterParams])
  const filteredTS = useMemo(() => timesheets.filter(ts => matchTS(ts, filterParams)), [timesheets, filterParams])
  const periodTS = useMemo(() => filteredTS.filter(ts => inPeriod(ts.date, filterParams)), [filteredTS, filterParams])

  const doneTasks = useMemo(() => filteredTasks.filter(t => t.stage === 'Done'), [filteredTasks])
  const periodDone = useMemo(() => doneTasks.filter(t => inPeriod(t.last_stage, filterParams)), [doneTasks, filterParams])
  const activeTasks = useMemo(() => filteredTasks.filter(t => t.stage !== 'Done' && t.stage !== 'Backlog'), [filteredTasks])
  const inProg = useMemo(() => filteredTasks.filter(t => t.stage === 'In Progress' || t.stage === 'In progress'), [filteredTasks])

  const kpis = useMemo(() => {
    const leads = periodDone.map(t => t.lead).filter((v): v is number => v !== null && v > 0)
    const cycles = periodDone.map(t => t.cycle).filter((v): v is number => v !== null && v > 0)
    const avgLead = leads.length ? leads.reduce((a, b) => a + b, 0) / leads.length : 0
    const avgCycle = cycles.length ? cycles.reduce((a, b) => a + b, 0) / cycles.length : 0

    const flowEffs = periodDone.map(t => t.flow_eff).filter((v): v is number => v !== null)
    const avgFlow = flowEffs.length ? flowEffs.reduce((a, b) => a + b, 0) / flowEffs.length : 0

    const msoH = periodTS.filter(t => t.cat === 'MSO').reduce((a, t) => a + t.hours, 0)
    const psoH = periodTS.filter(t => t.cat === 'PSO').reduce((a, t) => a + t.hours, 0)
    const intH = periodTS.filter(t => t.cat === 'Internal').reduce((a, t) => a + t.hours, 0)
    const totalH = msoH + psoH + intH
    const billPct = totalH > 0 ? ((msoH + psoH) / totalH) * 100 : 0

    const meetH = periodTS.filter(t => t.is_meeting).reduce((a, t) => a + t.hours, 0)
    const meetPct = totalH > 0 ? (meetH / totalH) * 100 : 0

    return {
      total: filteredTasks.length, inProg: inProg.length, completed: periodDone.length,
      avgLead, avgCycle, avgFlow, totalH, billPct, meetH, meetPct,
      msoH, psoH, intH, leads, cycles,
    }
  }, [filteredTasks, periodDone, periodTS, inProg])

  return {
    filteredTasks, filteredTS, periodTS, doneTasks, periodDone, activeTasks, inProg,
    kpis, filterParams,
  }
}
